import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router";

import { datadogRum } from "@datadog/browser-rum";
import { useAccessPattern } from "@hooks/useAccessPattern";
import { useOpenNewEndorsement } from "@hooks/endorsement";
import { useAppDispatch } from "@redux/hooks";
import { discardEndorsements } from "@redux/slices/endorsement";
import {
  EndorsementMode,
  EndorsementRoutes,
  navigateToEndorsementStep,
} from "@routing/paths";
import { PolicyDetailsContentProps } from "../../../types";

export const useEndorsementsList = (
  policy?: PolicyDetailsContentProps["policy"]
) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { canManageEndorsements } = useAccessPattern();

  const {
    openNewEndorsement,
    isEligibilityLoading,
    blockingReason,
    setBlockingReason,
  } = useOpenNewEndorsement(policy?.policyLocator);

  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);
  const [discardLocator, setDiscardLocator] = useState<string | null>(null);

  const handleCreateNewEndorsement = useCallback(
    () => openNewEndorsement(),
    [openNewEndorsement]
  );

  const openEndorsementModal = useCallback(
    async (
      mode: EndorsementMode,
      route: EndorsementRoutes,
      endorsementId: string
    ) => {
      await navigateToEndorsementStep(
        navigate,
        policy?.policyLocator ?? "",
        route,
        { dispatch, mode, endorsementId }
      );
    },
    [navigate, policy?.policyLocator, dispatch]
  );

  const handleDiscard = useCallback((endorsementLocator: string) => {
    setDiscardLocator(endorsementLocator);
    setIsDiscardModalOpen(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setIsDiscardModalOpen(false);
    setDiscardLocator(null);
  }, []);

  const handleDiscardConfirm = useCallback(async () => {
    if (!discardLocator || !policy?.policyLocator) return;
    try {
      await dispatch(
        discardEndorsements({
          policyLocator: policy.policyLocator,
          endorsementLocators: [discardLocator],
        })
      ).unwrap();
    } catch (e) {
      datadogRum.addError("Failed to discard endorsement", { e });
    } finally {
      handleModalClose();
    }
  }, [dispatch, discardLocator, policy?.policyLocator, handleModalClose]);

  const sortedEndorsements = useMemo(() => {
    const endorsements = policy?.endorsements ?? [];
    return endorsements.toSorted((a, b) =>
      b.effectiveDate.localeCompare(a.effectiveDate)
    );
  }, [policy?.endorsements]);

  return {
    canManageEndorsements,
    isEligibilityLoading,
    blockingReason,
    isDiscardModalOpen,
    handleCreateNewEndorsement,
    openEndorsementModal,
    handleDiscard,
    handleModalClose,
    handleDiscardConfirm,
    sortedEndorsements,
    setBlockingReason,
  };
};
